import { useEffect, useRef, useState } from 'react';

/**
 * useDictation — robust browser-based voice dictation hook.
 *
 * Wraps Web Speech API with proper error handling, iframe detection,
 * auto-stop, and a visible recording state. Replaces the silent-failing
 * inline implementations that previously existed in ReportBuilder/ParentSummary.
 *
 * Returns { recordingField, error, supported, inIframe, start, stop, dismissError }
 *
 * Usage:
 *   const { start, recordingField, error, supported, inIframe, dismissError } = useDictation({
 *     onTranscript: (fieldKey, text) => updateMyField(fieldKey, text),
 *     maxSeconds: 30,
 *   });
 *   <button onClick={() => start('focus')}>Dictate</button>
 */
export default function useDictation({ onTranscript, maxSeconds = 30 } = {}) {
  const [recordingField, setRecordingField] = useState(null);
  const [error, setError] = useState(null);
  const recRef = useRef(null);
  const timerRef = useRef(null);

  const SR =
    typeof window !== 'undefined'
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = !!SR;
  const inIframe = typeof window !== 'undefined' && window.self !== window.top;

  // Cleanup on unmount
  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (recRef.current) {
      try { recRef.current.abort(); } catch {/* ignore */}
    }
  }, []);

  const friendlyError = (raw) => {
    switch (raw) {
      case 'not-allowed':
      case 'service-not-allowed':
        return inIframe
          ? 'Microphone is blocked in this preview frame. Open in a new tab to dictate.'
          : 'Microphone permission denied. Click the lock icon in your browser address bar → allow microphone, then retry.';
      case 'no-speech':
        return 'No speech detected. Try again and speak after the indicator turns red.';
      case 'audio-capture':
        return 'No microphone found. Check your input device and retry.';
      case 'network':
        return 'Network error reaching the speech service. Check your connection.';
      case 'aborted':
        return null; // user cancelled — silent
      default:
        return `Dictation error (${raw || 'unknown'}). Please try again.`;
    }
  };

  const stop = () => {
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    if (recRef.current) {
      try { recRef.current.stop(); } catch {/* ignore */}
    }
    setRecordingField(null);
  };

  const start = (fieldKey) => {
    setError(null);

    if (!supported) {
      setError(
        'Your browser doesn\'t support voice dictation. Try Chrome, Edge, or Safari (iOS 14.5+).'
      );
      return;
    }

    // If already listening, stop first (toggle behavior)
    if (recordingField) {
      stop();
      return;
    }

    let rec;
    try {
      rec = new SR();
      rec.lang = 'en-US';
      rec.interimResults = false;
      rec.continuous = false;
    } catch (e) {
      setError(`Couldn't initialize dictation: ${e.message || e.name}`);
      return;
    }

    rec.onresult = (e) => {
      try {
        const transcript = Array.from(e.results)
          .map((r) => r[0]?.transcript || '')
          .join(' ')
          .trim();
        if (transcript && onTranscript) onTranscript(fieldKey, transcript);
      } catch (err) {
        setError(`Transcription failed: ${err.message}`);
      }
    };
    rec.onerror = (e) => {
      const msg = friendlyError(e.error);
      if (msg) setError(msg);
    };
    rec.onend = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      setRecordingField(null);
      recRef.current = null;
    };

    try {
      rec.start();
      recRef.current = rec;
      setRecordingField(fieldKey);
      // Auto-stop safety net
      timerRef.current = setTimeout(() => {
        try { rec.stop(); } catch {/* ignore */}
      }, maxSeconds * 1000);
    } catch (e) {
      // start() throws synchronously when permission is blocked or already running
      const name = e?.name || 'Error';
      if (name === 'InvalidStateError') {
        setError('Already listening. Click again to stop.');
      } else if (name === 'NotAllowedError') {
        setError(friendlyError('not-allowed'));
      } else {
        setError(`Couldn't start dictation: ${e?.message || name}`);
      }
      setRecordingField(null);
      recRef.current = null;
    }
  };

  const dismissError = () => setError(null);

  return { recordingField, error, supported, inIframe, start, stop, dismissError };
}
